<template>
  <v-card>
    <!-- Header -->
    <v-card-title class="d-flex align-center grey lighten-2">
      <v-icon color="blue darken-1" class="mr-3">mdi-shield-check</v-icon>
      <span class="text-h6--small">{{ $t('twoFactor.setup_2fa_authentication') }}</span>
    </v-card-title>
    <p v-if="setup.qrCode && !isMobile" class="text-right orange--text text--darken-2 pr-3 font-italic">
      {{ $t('twoFactor.download_authenticator_app') }}
    </p>

    <div v-show="isMobile">
      <!-- Content MOBILE -->
      <v-card-text class="pt-4">
        <!-- Step: Setup -->
        <div v-show="step === 'setup' && setup">
          <v-card v-if="setup.qrCode">
            <!-- TABS GOOGLE / MICROSOFT -->
            <v-tabs v-model="tab" background-color="dark-blue accent-2" dark fixed-tabs>
              <v-tabs-slider></v-tabs-slider>

              <v-tab href="#google">
                <v-icon>mdi-google</v-icon>
                <!-- Google -->
              </v-tab>

              <v-tab href="#microsoft">
                <v-icon>mdi-microsoft</v-icon>
                <!-- Microsoft -->
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab">
              <!-- GOOGLE -->
              <v-tab-item value="google">
                <v-card flat>
                  <v-card-text class="text-left">
                    <h4 class="mb-2">Google Authenticator</h4>
                    <ol>
                      <li>{{ $t('twoFactor.instructions.google.install') }}</li>
                      <li>{{ $t('twoFactor.instructions.google.add') }}</li>
                      <li>{{ $t('twoFactor.instructions.google.configuration_key') }}</li>
                      <li>{{ $t('twoFactor.instructions.google.configuration_name') }}</li>
                      <li>{{ $t('twoFactor.instructions.google.configuration_secret') }}</li>
                      <li>{{ $t('twoFactor.instructions.google.configuration_add') }}</li>
                    </ol>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- MICROSOFT -->
              <v-tab-item value="microsoft">
                <v-card flat>
                  <v-card-text class="text-left">
                    <h4 class="mb-2">Microsoft Authenticator</h4>
                    <ol>
                      <li>{{ $t('twoFactor.instructions.microsoft.install') }}</li>
                      <li>{{ $t('twoFactor.instructions.microsoft.scan_qr') }}</li>
                      <li>{{ $t('twoFactor.instructions.microsoft.configuration_code') }}</li>
                      <li>{{ $t('twoFactor.instructions.microsoft.configuration_choose') }}</li>
                      <li>{{ $t('twoFactor.instructions.microsoft.configuration_name') }}</li>
                      <li>{{ $t('twoFactor.instructions.microsoft.configuration_finish') }}</li>
                    </ol>
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card>

          <marquee scrolldelay="150" v-if="setup.qrCode" class="text-center orange--text text--darken-2 pr-3 font-italic pt-3">
            {{ $t('twoFactor.copy_secret') }}
          </marquee>

          <!-- SECRET TO COPY -->
          <div v-if="setup.qrCode" class="backup-codes-container">
            <v-btn icon x-small class="copy-button-mobile" @click="handleCopySecret">
              <v-icon x-small :color="copiedSecret ? 'green' : 'grey'">
                {{ copiedSecret ? 'mdi-check' : 'mdi-content-copy' }}
              </v-icon>
            </v-btn>
            <div class="backup-codes-box">
              <code class="backup-code">
                <div class="backup-codes-grid-mobile">
                  <span>
                    {{ falseSecret }}
                  </span>
                </div>
              </code>
            </div>
          </div>

          <div class="mb-4 d-flex flex-column align-center justify-center">
            <label class="text-body-2 font-weight-medium grey--text text--darken-2 d-block mb-2">
              {{ $t('twoFactor.verification_code') }}
            </label>

            <v-text-field
              v-model="verificationCode"
              placeholder="123456"
              outlined
              dense
              maxlength="6"
              class="centered-input"
              @input="onVerificationCodeInput"
              @keyup.enter="handleVerify"
              autofocus
              style="width: 50%; max-width: 240px" />
          </div>

          <v-alert
            v-if="totalAttempts <= 2 && totalAttempts !== null"
            type="warning"
            dense
            outlined
            class="mb-4 pr-6"
            style="width: max-content; margin: 0 auto; font-size: small">
            <p class="multiline">{{ attemptsMessage }}</p>
          </v-alert>

          <v-btn
            style="width: max-content; display: block; margin: 0 auto"
            dense
            x-small
            color="primary"
            :disabled="verificationCode.length !== 6 || isVerifying"
            :loading="isVerifying"
            @click="handleVerify">
            {{ isVerifying ? $t('twoFactor.verifying') : $t('twoFactor.verify_code') }}
          </v-btn>
        </div>

        <!-- Step: Backup -->
        <div v-show="step === 'backup' && setup">
          <div class="text-center mb-4">
            <v-icon size="48" color="green">mdi-check-circle</v-icon>
            <h3 class="text-h6 mt-3">{{ $t('twoFactor.verification_success') }}</h3>
            <p class="text-body-2 grey--text">{{ $t('twoFactor.keep_backup_codes') }}</p>
          </div>

          <v-alert type="warning" dense outlined class="mb-4" style="width: 80%; margin: 0 auto">
            <div class="d-flex">
              <div>
                <p class="text-body-2 font-weight-medium mb-1">{{ $t('user.atribute.access_warning') }}:</p>
                <ul class="text-caption pl-4 mb-0">
                  <li>{{ $t('twoFactor.one_time_code') }}</li>
                  <li>{{ $t('twoFactor.secure_offline') }}</li>
                </ul>
              </div>
            </div>
          </v-alert>

          <div class="backup-codes-container">
            <v-btn text x-small color="primary" @click="downloadBackupCodes">
              <v-icon x-small class="mr-1">mdi-download</v-icon>
              {{ $t('twoFactor.download') }}
            </v-btn>
            <v-btn icon x-small class="copy-button" @click="handleCopyAllBackupCodes">
              <v-icon x-small :color="copiedAll ? 'green' : 'grey'">
                {{ copiedAll ? 'mdi-check' : 'mdi-content-copy' }}
              </v-icon>
            </v-btn>
            <div class="backup-codes-box">
              <code class="backup-code">
                <div class="backup-codes-grid">
                  <span v-for="(code, index) in setup.backupCodes" :key="index">
                    {{ code }}
                  </span>
                </div>
              </code>
            </div>
          </div>

          <div class="d-flex justify-center align-center" style="height: 100%">
            <v-btn small color="success" @click="loginUser"> {{ $t('general.form.accept') }} </v-btn>
          </div>
        </div>
      </v-card-text>
    </div>

    <div v-show="!isMobile">
      <!-- Content PC -->
      <v-card-text class="pt-4">
        <!-- Step: Setup -->
        <div v-if="step === 'setup' && setup">
          <div v-if="setup.qrCode" class="text-center mb-4">
            <p class="text-body-2 grey--text mb-4">{{ $t('twoFactor.scan_qr_code') }}</p>
            <div class="d-inline-block pa-4 white rounded-lg elevation-2">
              <img :src="setup.qrCode" alt="2FA QR code" width="192" height="192" @error="onQrError" @load="onQrLoad" />
            </div>
          </div>

          <div class="mb-4 d-flex flex-column align-center justify-center">
            <label class="text-body-2 font-weight-medium grey--text text--darken-2 d-block mb-2">
              {{ $t('twoFactor.verification_code') }}
            </label>

            <v-text-field
              v-model="verificationCode"
              placeholder="123456"
              outlined
              dense
              maxlength="6"
              class="centered-input"
              @input="onVerificationCodeInput"
              @keyup.enter="handleVerify"
              autofocus
              style="width: 50%; max-width: 240px" />
          </div>

          <v-alert
            v-if="totalAttempts <= 2 && totalAttempts !== null"
            type="warning"
            dense
            outlined
            class="mb-4 pr-6"
            style="width: max-content; margin: 0 auto; font-size: small">
            <p class="multiline">{{ attemptsMessage }}</p>
          </v-alert>

          <v-btn
            style="width: max-content; display: block; margin: 0 auto"
            dense
            x-small
            color="primary"
            :disabled="verificationCode.length !== 6 || isVerifying"
            :loading="isVerifying"
            @click="handleVerify">
            {{ isVerifying ? $t('twoFactor.verifying') : $t('twoFactor.verify_code') }}
          </v-btn>
        </div>

        <!-- Step: Backup -->
        <div v-if="step === 'backup' && setup">
          <div class="text-center mb-4">
            <v-icon size="48" color="green">mdi-check-circle</v-icon>
            <h3 class="text-h6 mt-3">{{ $t('twoFactor.verification_success') }}</h3>
            <p class="text-body-2 grey--text">{{ $t('twoFactor.keep_backup_codes') }}</p>
          </div>

          <v-alert type="warning" dense outlined class="mb-4" style="width: 80%; margin: 0 auto">
            <div class="d-flex">
              <div>
                <p class="text-body-2 font-weight-medium mb-1">{{ $t('user.atribute.access_warning') }}:</p>
                <ul class="text-caption pl-4 mb-0">
                  <li>{{ $t('twoFactor.one_time_code') }}</li>
                  <li>{{ $t('twoFactor.secure_offline') }}</li>
                </ul>
              </div>
            </div>
          </v-alert>

          <div class="backup-codes-container">
            <v-btn text x-small color="primary" @click="downloadBackupCodes">
              <v-icon x-small class="mr-1">mdi-download</v-icon>
              {{ $t('twoFactor.download') }}
            </v-btn>
            <v-btn icon x-small class="copy-button" @click="handleCopyAllBackupCodes">
              <v-icon x-small :color="copiedAll ? 'green' : 'grey'">
                {{ copiedAll ? 'mdi-check' : 'mdi-content-copy' }}
              </v-icon>
            </v-btn>
            <div class="backup-codes-box">
              <code class="backup-code">
                <div class="backup-codes-grid">
                  <span v-for="(code, index) in setup.backupCodes" :key="index">
                    {{ code }}
                  </span>
                </div>
              </code>
            </div>
          </div>

          <div class="d-flex justify-center align-center" style="height: 100%">
            <v-btn x-small color="success" @click="loginUser"> {{ $t('general.form.accept') }} </v-btn>
          </div>
        </div>
      </v-card-text>
    </div>

    <!-- Footer -->
    <v-divider />
    <v-card-actions class="pa-4">
      <v-btn x-small text color="red" @click="handleCancel" style="margin: auto; width: max-content">
        {{ $t('general.form.cancel') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { verifyTwoFactor } from '../server/petitions/users/twoFactor';
import router from '../plugins/router/index';
import userViewModel from '../viewModels/userViewModel';
import { eventBus } from '../main';

export default {
  name: 'TwoFactorQR',
  props: {
    email: {
      type: String,
      required: true,
    },
    setup: {
      type: Object,
      default: () => ({
        qrCode: '',
        secret: '',
        backupCodes: [],
      }),
    },
    user: {
      type: Object,
      required: true,
    },
    isMobile: {
      type: Boolean,
    },
  },
  data() {
    return {
      step: 'setup', // 'setup' | 'backup'
      verificationCode: '',
      isVerifying: false,
      totalAttempts: null,
      secretCode: null,
      falseSecret: null,
      tab: 'google',
      copiedAll: null,
      copiedSecret: null,
    };
  },

  mounted() {
    this.totalAttempts = this.user?.restrictions?.features?.two_factor_auth?.attempts;
    this.secretCode = this.setup.secret;
    this.falseSecret = this.secretCode.slice(0, 10).concat('...');
  },

  computed: {
    attemptsMessage() {
      if (this.totalAttempts === null) return '';

      if (this.totalAttempts <= 0) {
        return this.$t('twoFactor.blocked_account.line_1') + '\n' + this.$t('twoFactor.blocked_account.line_2');
      }

      if (this.totalAttempts <= 2) {
        return `${this.$t('twoFactor.remaining_attempts')}: ${this.totalAttempts}`;
      }

      return '';
    },
  },

  methods: {
    onVerificationCodeInput(value) {
      // Solo permitir dígitos y máximo 6 caracteres
      this.verificationCode = value.replace(/\D/g, '').slice(0, 6);
    },
    // ELIMINAR LOGS
    onQrError(e) {
      // console.error('❌ Error cargando imagen QR:', e);
      // console.log('🔍 URL que falló:', this.setup.qrCode);
    },
    onQrLoad() {
      // console.log('✅ Imagen QR cargada correctamente', this.setup);
    },

    async handleCopyAllBackupCodes() {
      const allCodes = this.setup.backupCodes.join('\n');

      try {
        await navigator.clipboard.writeText(allCodes);
        this.copiedAll = true;

        setTimeout(() => {
          this.copiedAll = false;
        }, 2000);
      } catch (error) {
        console.error('Error copiando al portapapeles:', error);
      }
    },
    async handleCopySecret() {
      try {
        await navigator.clipboard.writeText(this.secretCode);

        this.copiedSecret = true;

        setTimeout(() => {
          this.copiedSecret = false;
        }, 2000);
      } catch (error) {
        console.error('Error copiando al portapapeles:', error);
      }
    },
    async handleVerify() {
      if (!this.verificationCode || this.verificationCode.length !== 6) {
        return;
      }

      this.isVerifying = true;

      const payload = {
        email: this.email,
        otpToken: this.verificationCode,
      };

      try {
        const result = await verifyTwoFactor(payload, this.user.token);

        this.totalAttempts = result.attempts;

        const hasBackupCodes = Array.isArray(this.setup.backupCodes) && this.setup.backupCodes.length > 0;

        if (result.valid === true) {
          if (hasBackupCodes) {
            this.step = 'backup';
          } else {
            userViewModel.SetToken(this.user.token);
            router.push({ path: '/' });
          }
        }
        if (result.valid === false) {
          eventBus.$emit('incorrect_2fa_code');
        }
      } catch (error) {
        eventBus.$emit('incorrect_2fa_code'); //DEBERIA MOSTRAR L SNACKBAR
        console.error(error);

        this.step = 'setup';
        this.verificationCode = '';
      } finally {
        this.isVerifying = false;
        this.verificationCode = '';
      }
    },

    downloadBackupCodes() {
      if (!this.setup.backupCodes || this.setup.backupCodes.length === 0) return;

      const content = this.setup.backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n');

      const blob = new Blob([`${this.$t('twoFactor.backup_codes')}\n\n${content}`], {
        type: 'text/plain',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'backup-codes-2fa.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    loginUser() {
      userViewModel.SetToken(this.user.token);
      this.$emit('confirm');
      router.push({ path: '/' });
    },
    handleCancel() {
      this.$emit('cancel');
      this.resetModal();
    },
    resetModal() {
      this.step = 'setup';
      this.verificationCode = '';
      this.isVerifying = false;
    },
  },
};
</script>

<style scoped>
.qr-placeholder {
  width: 192px;
  height: 192px;
  background-color: #f5f5f5;
  border: 2px dashed #ccc;
}

.centered-input >>> input {
  text-align: center;
  font-family: monospace;
  font-size: 1.25rem;
  letter-spacing: 0.5em;
}

.backup-codes-container {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0px;
  width: 50%;
  height: max-content;
  max-height: 160px;
  margin: 0 auto 16px auto;
  overflow-y: auto;
  background-color: #fafafa;
}

/* Mobile */
@media (max-width: 600px) {
  .backup-codes-container {
    width: 80%;
  }
}

.backup-codes-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.backup-code {
  display: block; /* importante */
  width: max-content;
  white-space: pre-line;
  font-family: monospace;
  flex: 1;
}

.backup-codes-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px 5px;
  place-items: center;
}

.backup-codes-grid-mobile {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 5px 5px;
  place-items: center;
}

.backup-code-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  margin-bottom: 6px;
  background-color: #f5f5f5;
  border-radius: 6px;
}

.backup-code-row:last-child {
  margin-bottom: 0;
}

.backup-code {
  font-family: monospace;
  font-size: 0.95rem;
  letter-spacing: 0.15em;
}

.copy-button {
  float: right;
  margin-right: 5px;
  background-color: rgba(191, 187, 187, 0.601);
}

.copy-button-mobile {
  position: relative;
  left: 86%;
  margin-left: 8px;
  background-color: rgba(191, 187, 187, 0.601);
}

.centered-input input {
  text-align: center;
  letter-spacing: 4px;
}

.multiline {
  margin: auto 0;
  white-space: pre-line;
}

.text-h6--small {
  font-size: 1rem;
  line-height: 1.2;
}
</style>
